// src/redux/slices/globalComponentsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { globalService } from '../../services/globalService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchGlobalConfig = createAsyncThunk(
    'global/fetchGlobalConfig',
    async (_, { rejectWithValue }) => {
        try {
            const response = await globalService.getGlobalConfig()
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateGlobalConfig = createAsyncThunk(
    'global/updateGlobalConfig',
    async (configData, { rejectWithValue }) => {
        try {
            const response = await globalService.updateGlobalConfig(configData)
            return response
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateNavigation = createAsyncThunk(
    'global/updateNavigation',
    async (navData, { rejectWithValue }) => {
        try {
            const response = await globalService.updateGlobalConfig({ nav: navData })
            return { nav: response.nav }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateFooter = createAsyncThunk(
    'global/updateFooter',
    async (footerData, { rejectWithValue }) => {
        try {
            const response = await globalService.updateGlobalConfig({ footer: footerData })
            return { footer: response.footer }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const globalComponentsSlice = createSlice({
    name: 'global',
    initialState: {
        nav: null,
        footer: null,
        buildSolution: null,
        cards: [],
        newsletterSection: null,
        themeSettings: null,
        socialLinks: [],
        contactInfo: null,
        companyInfo: null,
        loading: false,
        updating: false,
        error: null,
        lastUpdated: null
    },
    reducers: {
        updateNavLocally: (state, action) => {
            state.nav = action.payload
        },
        updateFooterLocally: (state, action) => {
            state.footer = action.payload
        },
        updatebuildSolution: (state, action) => {
            state.buildSolution = action.payload
        },
        updateCards: (state, action) => {
            state.cards = action.payload
        },
        updateNewsletterSection: (state, action) => {
            state.newsletterSection = action.payload
        },
        updateThemeSettings: (state, action) => {
            state.themeSettings = action.payload
        },
        updateSocialLinks: (state, action) => {
            state.socialLinks = action.payload
        },
        updateContactInfo: (state, action) => {
            state.contactInfo = action.payload
        },
        updateCompanyInfo: (state, action) => {
            state.companyInfo = action.payload
        },
        clearError: (state) => {
            state.error = null
        },
        clearGlobalData: (state) => {
            state.nav = null
            state.footer = null
            state.buildSolution = null
            state.cards = []
            state.newsletterSection = null
            state.themeSettings = null
            state.socialLinks = []
            state.contactInfo = null
            state.companyInfo = null
            state.lastUpdated = null
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Global Config
            .addCase(fetchGlobalConfig.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchGlobalConfig.fulfilled, (state, action) => {
                state.loading = false
                const {
                    nav,
                    footer,
                    buildSolution,
                    cards,
                    newsletterSection,
                    themeSettings,
                    socialLinks,
                    contactInfo,
                    companyInfo
                } = action.payload?.data?.config
                state.nav = nav || state.nav
                state.footer = footer || state.footer
                state.buildSolution = buildSolution || state.buildSolution
                state.cards = cards || state.cards
                state.newsletterSection = newsletterSection || state.newsletterSection
                state.themeSettings = themeSettings || state.themeSettings
                state.socialLinks = socialLinks || state.socialLinks
                state.contactInfo = contactInfo || state.contactInfo
                state.companyInfo = companyInfo || state.companyInfo
                state.lastUpdated = new Date().toISOString()
                state.error = null
            })
            .addCase(fetchGlobalConfig.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })
            // Update Global Config
            .addCase(updateGlobalConfig.pending, (state) => {
                state.updating = true
                state.error = null
            })
            .addCase(updateGlobalConfig.fulfilled, (state, action) => {
                state.updating = false
                const {
                    nav,
                    footer,
                    buildSolution,
                    cards,
                    newsletterSection,
                    themeSettings,
                    socialLinks,
                    contactInfo,
                    companyInfo
                } = action.payload

                if (nav) state.nav = nav
                if (footer) state.footer = footer
                if (buildSolution) state.buildSolution = buildSolution
                if (cards) state.cards = cards
                if (newsletterSection) state.newsletterSection = newsletterSection
                if (themeSettings) state.themeSettings = themeSettings
                if (socialLinks) state.socialLinks = socialLinks
                if (contactInfo) state.contactInfo = contactInfo
                if (companyInfo) state.companyInfo = companyInfo

                state.lastUpdated = new Date().toISOString()
                state.error = null
            })
            .addCase(updateGlobalConfig.rejected, (state, action) => {
                state.updating = false
                state.error = action.payload
            })
            // Update Navigation
            .addCase(updateNavigation.pending, (state) => {
                state.updating = true
                state.error = null
            })
            .addCase(updateNavigation.fulfilled, (state, action) => {
                state.updating = false
                state.nav = action.payload.nav
                state.lastUpdated = new Date().toISOString()
                state.error = null
            })
            .addCase(updateNavigation.rejected, (state, action) => {
                state.updating = false
                state.error = action.payload
            })
            // Update Footer
            .addCase(updateFooter.pending, (state) => {
                state.updating = true
                state.error = null
            })
            .addCase(updateFooter.fulfilled, (state, action) => {
                state.updating = false
                state.footer = action.payload.footer
                state.lastUpdated = new Date().toISOString()
                state.error = null
            })
            .addCase(updateFooter.rejected, (state, action) => {
                state.updating = false
                state.error = action.payload
            })
    }
})

export const {
    updateNavLocally,
    updateFooterLocally,
    updatebuildSolution,
    updateCards,
    updateNewsletterSection,
    updateThemeSettings,
    updateSocialLinks,
    updateContactInfo,
    updateCompanyInfo,
    clearError,
    clearGlobalData
} = globalComponentsSlice.actions

export default globalComponentsSlice.reducer